import { useCallback, useMemo } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/chillnn/cn";
import { getGiftDetailUrl, getReservationDetail } from "@/lib/chillnn/get-detail-url";
import type {
	VoucherIssuedForCampaignListByHotelIdAndVoucherId_DetailsPageQuery,
	VoucherIssuedMarkVoucherAsUnUsedInput,
	VoucherIssuedMarkVoucherAsUsedInput,
} from "@/lib/gql/__generated__/graphql";
import { useCopyToClipboard } from "@/lib/react-use/use-copy-to-clipboard";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import type { ButtonVariantType } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";
import { SwitchField } from "@/components/ui/form/fields/switch-field";
import { TableCell, TableRow } from "@/components/ui/table/table";

type Props = {
	hotelID: string;
	campaignItem: VoucherIssuedForCampaignListByHotelIdAndVoucherId_DetailsPageQuery["voucherIssuedForCampaignListByHotelIDAndVoucherID"]["voucherIssuedForCampaignList"][0];
	onUpdateMarkVoucherAsUnUsed: (input: VoucherIssuedMarkVoucherAsUnUsedInput) => Promise<boolean>;
	onUpdateMarkVoucherAsUsed: (input: VoucherIssuedMarkVoucherAsUsedInput) => Promise<boolean>;
};

export const CampaignDetailsItem = ({
	hotelID,
	campaignItem,
	onUpdateMarkVoucherAsUnUsed,
	onUpdateMarkVoucherAsUsed,
}: Props) => {
	const { voucherCode, guestName, guestEmail, usedReservationID } = campaignItem;

	// 使用済みのバウチャーかどうか
	const isUsedVoucher = !!campaignItem.usedAt; // || !!campaignItem.usedReservationID;

	const onSwitchChange = useCallback(async () => {
		if (isUsedVoucher) {
			// 使用済みの場合は未使用にする

			await onUpdateMarkVoucherAsUnUsed({
				hotelID,
				voucherCode,
			});
		} else {
			// 未使用の場合は使用済みにする
			await onUpdateMarkVoucherAsUsed({
				hotelID,
				voucherCode,
			});
		}
	}, [hotelID, isUsedVoucher, onUpdateMarkVoucherAsUnUsed, onUpdateMarkVoucherAsUsed, voucherCode]);

	const reservationDetailPagePath = usedReservationID ? getReservationDetail(hotelID, usedReservationID) : undefined;

	const { copy } = useCopyToClipboard();
	const handleCopy = useCallback(() => {
		// ゲスト向けのギフト詳細ページのURL
		const url = getGiftDetailUrl(hotelID, voucherCode);

		copy(url);

		notifyUserEventInfo({
			title: "クリップボードにコピーしました",
		});
	}, [voucherCode, copy, hotelID]);

	const buttonVariant: ButtonVariantType = useMemo(() => (isUsedVoucher ? "disabled" : "outline"), [isUsedVoucher]);

	return (
		<TableRow key={voucherCode}>
			<TableCell>
				<div className="flex w-24 flex-col items-start gap-y-2 text-center">
					<SwitchField value={isUsedVoucher} handleInput={onSwitchChange} />
					<div className="flex items-center justify-center gap-x-1 text-[12px]">
						<p className={cn(isUsedVoucher ? "text-zinc-400" : "font-semibold text-error-600")}>未使用</p>
						<p>/</p>
						<p className={cn(isUsedVoucher ? "font-semibold text-success-600" : "text-zinc-400")}>使用済</p>
					</div>
				</div>
			</TableCell>
			<TableCell>
				<p className="w-32">{voucherCode}</p>
			</TableCell>
			<TableCell>
				<p className="line-clamp-2 w-40 ">{guestName}</p>
			</TableCell>
			<TableCell>
				<p className="w-52 break-words">{guestEmail}</p>
			</TableCell>
			<TableCell>
				<div className="w-24 break-words">
					{reservationDetailPagePath && (
						<Link
							to={reservationDetailPagePath}
							target="_blank"
							className="text-link-400 underline-offset-4 hover:underline"
							viewTransition
						>
							{usedReservationID}
						</Link>
					)}
				</div>
			</TableCell>
			<TableCell>
				<div className="w-20">
					<Button variant={buttonVariant} type="button" onClick={handleCopy} size="sm">
						コピー
					</Button>
				</div>
			</TableCell>
		</TableRow>
	);
};

CampaignDetailsItem.displayName = "CampaignDetailsItem";
